import { createAsyncThunk, createSlice, isRejectedWithValue, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import { dashboardService } from "./services/dashboard.service";
import { IProvision } from "./domain/IProvision";
import { settingsService } from "./services/settings.service";
import { IUpdateProvisionRequestParams } from "./domain/IUpdateProvisionRequestParams";
import { IAddHourlyRateNameRequestParams } from "./domain/hourly_work/IAddHourlyRateNameRequestParams";
import { IChangeHourlyRateNameRequestParams } from "./domain/hourly_work/IChangeHourlyRateNameRequestParams";

export const getUnseenMessagesForEmployee = createAsyncThunk(
  "Dashboard/getUnseenMessagesForEmployee",
  async () => {
    try {
      const response = await dashboardService.getNumberOfUnseenMessages();
      return response.data.unseenMessagesCount;
    } catch (err) {
      setError(err);
    }
  }
);

export const getNotAcceptedRequisitions = createAsyncThunk(
  "Dashboard/getNotAcceptedRequisitions",
  async () => {
    try {
      const response = await dashboardService.getNumberOfNotAcceptedRequisitions();
      return response.data.requisitionsCount;
    } catch (err) {
      setError(err);
    }
  }
);

export const getUnseenReports = createAsyncThunk(
  "Dashboard/getUnseenReports",
  async () => {
    try {
      const response = await dashboardService.getNumberOfUnseenReports();
      return response.data.unseenReportsCount;
    } catch (err) {
      setError(err);
    }
  }
);

export const getProvision = createAsyncThunk(
  "Dashboard/getProvision",
  async () => {
    try {
      const response = await settingsService.getProvision();
      return response;
    } catch (err) {
      setError(err);
    }
  }
);

export const updateProvision = createAsyncThunk(
  "Dashboard/updateProvision",
  async (params: IUpdateProvisionRequestParams) => {
    try {
      const response = await settingsService.updateProvision(params);
      return response;
    } catch (err) {
      setError(err);
    }
  }
);

export const addHourlyRateName = createAsyncThunk(
  "Dashboard/addHourlyRateName",
  async (params: IAddHourlyRateNameRequestParams) => {
    try {
      const response = await settingsService.addHourlyRateName(params);
      return response;
    } catch (err) {
      setError(err);
    }
  }
);

export const changeHourlyRateName = createAsyncThunk(
  "Dashboard/changeHourlyRateName",
  async (params: IChangeHourlyRateNameRequestParams) => {
    try {
      const response = await settingsService.changeHourlyRateName(params);
      return response;
    } catch (err) {
      setError(err);
    }
  }
);

interface dashboardState {
  loading: boolean;
  unseenMessagesCount: number;
  requisitionsCount: number;
  unseenReportsCount: number;
  provisions: IProvision | undefined,
  error: any;
};

const initialState: dashboardState = {
  loading: false,
  unseenMessagesCount: 0,
  requisitionsCount: 0,
  unseenReportsCount: 0,
  provisions: undefined,
  error: null,
};

const rejectionReducer = (state: { error: any, loading: boolean; }, { payload }: any) => {
  const errorArray = Object.values(payload.message).map(
    (key: any) => key.message
  );
  state.error = errorArray;
  state.loading = true;
};

export const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setUnseenMessagesNumber: (state, action: PayloadAction<number>) => {
      state.unseenMessagesCount = 0;
    },
    setAcceptedRequisitionsNumber: (state, action: PayloadAction<boolean>) => {
      if (action.payload)
        state.requisitionsCount -= 1;
    },
    updateProvisionTemporarily: (state, action: PayloadAction<IUpdateProvisionRequestParams>) => {
      if (action.payload) {
        if (state.provisions === undefined) {
          state.provisions = {
            sickLeaveCarnets: undefined,
            vacationCarnets: undefined,
            allHourlyRateNames: [],
            activeHourlyRateNames: []
          };
        }
        if (action.payload?.sickLeaveCarnets !== undefined) {
          state.provisions.sickLeaveCarnets = action.payload.sickLeaveCarnets;
        }
        if (action.payload?.vacationCarnets !== undefined) {
          state.provisions.vacationCarnets = action.payload.vacationCarnets;
        }
      }
    },
    updateHourlyRateNameTemporarily: (state, action: PayloadAction<IChangeHourlyRateNameRequestParams>) => {
      if (action.payload) {
        if (state.provisions === undefined) {
          state.provisions = {
            sickLeaveCarnets: undefined,
            vacationCarnets: undefined,
            allHourlyRateNames: [],
            activeHourlyRateNames: []
          };
        }
        if (action.payload?.label !== undefined) {
          state.provisions.allHourlyRateNames[action.payload.index].name = action.payload.label;
        }
        if (action.payload?.active !== undefined) {
          state.provisions.allHourlyRateNames[action.payload.index].active = action.payload.active;
        }
      }
    },
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUnseenMessagesForEmployee.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getUnseenMessagesForEmployee.fulfilled, (state, action) => {
      if (action.payload) {
        state.unseenMessagesCount = action.payload;
      }
      state.loading = false;
    });
    builder.addCase(getNotAcceptedRequisitions.fulfilled, (state, action) => {
      if (action.payload) {
        state.requisitionsCount = action.payload;
      }
      state.loading = false;
    });
    builder.addCase(getUnseenReports.fulfilled, (state, action) => {
      if (action.payload) {
        state.unseenReportsCount = action.payload;
      }
      state.loading = false;
    });
    builder.addCase(getProvision.fulfilled, (state, action) => {
      if (action.payload) {
        state.provisions = action.payload;
      }
    });
    builder.addCase(updateProvision.fulfilled, (state, action) => {
      if (action.payload) {
        state.provisions = action.payload;
      }
    });
    builder.addCase(addHourlyRateName.fulfilled, (state, action) => {
      if (action.payload) {
        state.provisions = action.payload;
      }
    });
    builder.addCase(changeHourlyRateName.fulfilled, (state, action) => {
      if (action.payload) {
        state.provisions = action.payload;
      }
    });
    builder.addMatcher(
      isRejectedWithValue(getUnseenMessagesForEmployee, getNotAcceptedRequisitions),
      rejectionReducer
    );
  },
});

export const selectState = (state: RootState) => state.dashboard;

export const {
  setLoading,
  setUnseenMessagesNumber,
  setAcceptedRequisitionsNumber,
  updateProvisionTemporarily,
  updateHourlyRateNameTemporarily,
  setError,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
